/** A definition-list panel of the facts about one thing (the detail page's "key facts" rail).
 *
 * A `<dl>` of label and value pairs, labels muted, values ink and tabular, so a reader scans down the
 * labels and across to the value. A value can be any node: a chip, an `EntityLink`, a mono hash. It
 * shows what it is handed; it computes nothing. A missing value is the caller's to say in words
 * (`Not set`), never an empty cell.
 */

import type { ReactNode } from "react";

import styles from "./KeyFacts.module.css";

export interface Fact {
  label: string;
  value: ReactNode;
  /** Render the value in the monospace face: hashes, ids, citations. */
  mono?: boolean;
  testId?: string;
}

export function KeyFacts({
  title,
  facts,
  layout = "stacked",
  action,
  testId,
  as: Heading = "h2",
}: {
  /** Panel heading. Rendered as a heading; omit when the page already has a heading above. */
  title?: string;
  facts: readonly Fact[];
  /** `stacked`: label over value. `inline`: label left, value right (the rail). */
  layout?: "stacked" | "inline";
  /** A link or button at the heading's right: `Edit`, `See all`. */
  action?: ReactNode;
  testId?: string;
  /** Heading level. A signed-in screen's h1 is the shell's, so the default is h2. */
  as?: "h2" | "h3";
}) {
  return (
    <section className={styles.panel} data-testid={testId}>
      {title || action ? (
        <header className={styles.head}>
          {title ? <Heading className={styles.title}>{title}</Heading> : <span />}
          {action}
        </header>
      ) : null}
      <dl className={`${styles.list} ${layout === "inline" ? styles.inline : styles.stacked}`}>
        {facts.map((fact) => (
          <div key={fact.label} className={styles.row} data-testid={fact.testId}>
            <dt className={styles.label}>{fact.label}</dt>
            <dd className={`${styles.value} ${fact.mono ? styles.mono : ""}`}>{fact.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
